import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { SubscribersTable } from '@/components/admin/SubscribersTable';
import { PaginationFooter } from '@/components/admin/PaginationFooter';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { adminSubscribersService } from '@/services/admin-subscribers.service';
import { adminSubscriberListsService } from '@/services/admin-subscriber-lists.service';
import { ApiError } from '@/services/http';
import type { SubscriberListView, SubscriberView } from '@/types/api';

const DEFAULT_PAGE_SIZE = 100;

export function AdminSubscriberListPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [listName, setListName] = useState('');
  const [subscribers, setSubscribers] = useState<SubscriberView[]>([]);
  const [lists, setLists] = useState<SubscriberListView[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [pendingDelete, setPendingDelete] = useState<SubscriberView | null>(null);

  useEffect(() => {
    adminSubscriberListsService.list().then(setLists).catch(() => {});
  }, []);

  const reload = () => {
    if (!id) return;
    setLoading(true);
    adminSubscriberListsService
      .members(id, { page, pageSize })
      .then((res) => {
        setListName(res.listName);
        setSubscribers(res.subscribers);
        setTotalCount(res.totalCount);
      })
      .catch((err) => {
        toast.error(err instanceof ApiError ? err.message : 'Could not load list.');
        navigate('/admin/subscribers/lists', { replace: true });
      })
      .finally(() => setLoading(false));
  };

  useEffect(reload, [id, page, pageSize]);

  const handleAssign = async (subId: string, listId: string | null) => {
    setBusyId(subId);
    try {
      await adminSubscribersService.assignList(subId, listId);
      // Moving a subscriber off this list removes it from the view.
      setSubscribers((prev) => prev.filter((s) => s.id !== subId || listId === id));
      if (listId !== id) setTotalCount((c) => Math.max(0, c - 1));
      toast.success('List updated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update list.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReactivate = async (subId: string) => {
    setBusyId(subId);
    try {
      const updated = await adminSubscribersService.reactivate(subId);
      setSubscribers((prev) => prev.map((s) => (s.id === subId ? updated : s)));
      toast.success('Subscriber reactivated.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not reactivate.');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    const target = pendingDelete;
    setPendingDelete(null);
    setBusyId(target.id);
    try {
      await adminSubscribersService.remove(target.id);
      setSubscribers((prev) => prev.filter((s) => s.id !== target.id));
      setTotalCount((c) => Math.max(0, c - 1));
      toast.success('Subscriber deleted.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/subscribers/lists')}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-or-slate-200 bg-white text-or-slate-600 hover:bg-or-slate-50"
            aria-label="Back to lists"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </button>
          <div>
            <div className="or-kicker text-or-orange-600">Admin · List</div>
            <h1 className="mt-0.5 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              {listName || 'List'}
            </h1>
            <p className="text-[13px] text-or-slate-600">
              {totalCount.toLocaleString()} member{totalCount === 1 ? '' : 's'}
            </p>
          </div>
        </div>

        <SubscribersTable
          subscribers={subscribers}
          lists={lists}
          busyId={busyId}
          loading={loading}
          onAssign={handleAssign}
          onReactivate={handleReactivate}
          onDelete={setPendingDelete}
        />

        <PaginationFooter
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          noun="members"
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
        />
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDelete(null);
        }}
        title="Delete this subscriber?"
        description={
          pendingDelete
            ? `This permanently removes ${pendingDelete.email} from your audience.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={handleDelete}
      />
    </AppShell>
  );
}
